<template>
  <div
    :class="[
      'p-4 border-b border-gray-200 cursor-pointer transition hover:bg-gray-50',
      active ? 'bg-blue-50 border-l-4 border-l-primary-500' : ''
    ]"
  >
    <div class="flex items-start justify-between">
      <!-- Avatar + Content -->
      <div class="flex items-start space-x-3 flex-1 min-w-0">
        <!-- Avatar -->
        <div class="w-12 h-12 rounded-full bg-primary-500 text-white flex items-center justify-center font-bold text-lg flex-shrink-0">
          {{ initials }}
        </div>
        
        <div class="flex-1 min-w-0">
          <!-- Name and Time -->
          <div class="flex items-center justify-between gap-2 mb-1">
            <div class="flex items-center gap-1.5 min-w-0">
              <h3 class="font-semibold text-gray-900 truncate">
                {{ conversation.contact_name || 'Sem nome' }}
              </h3>
              <span 
                v-if="conversation.lead_replied === 1" 
                class="flex-shrink-0 inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-medium"
                title="Lead respondeu — aguardando ação"
              >
                <span class="relative flex h-1.5 w-1.5">
                  <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span class="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500"></span>
                </span>
                💬
              </span>
            </div>
            <span class="text-xs text-gray-500 flex-shrink-0">
              {{ formatTime(conversation.last_message_at) }}
            </span>
          </div>
          
          <!-- Last Message Preview -->
          <p class="text-sm text-gray-600 truncate mb-1">
            {{ lastMessagePreview }}
          </p>
          
          <!-- Tags -->
          <div class="flex items-center gap-2 flex-wrap">
            <span
              v-if="conversation.lead_status"
              :class="[
                'text-xs px-2 py-0.5 rounded-full',
                statusClass(conversation.lead_status)
              ]"
            >
              {{ leadStatusLabel(conversation.lead_status) }}
            </span>
          </div>
        </div>
      </div>
      
      <!-- Unread Badge -->
      <div v-if="conversation.unread_count > 0" class="ml-2 flex-shrink-0">
        <span class="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 text-xs font-bold text-white bg-primary-500 rounded-full">
          {{ conversation.unread_count }}
        </span>
      </div>
      <!-- Admin Actions (Only on Hover) -->
      <div v-if="isAdmin && active" class="ml-2 flex flex-col gap-1 items-center justify-center">
        <!-- Unarchive Button -->
        <button 
          v-if="conversation.status === 'archived'"
          @click.stop="unarchiveConversation" 
          class="p-1 rounded hover:bg-green-100 text-green-600"
          title="Desarquivar (Restaurar)"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
          </svg>
        </button>

        <!-- Archive Button -->
        <button 
          v-else
          @click.stop="archiveConversation" 
          class="p-1 rounded hover:bg-yellow-100 text-yellow-600"
          title="Arquivar Conversa"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
            <path stroke-linecap="round" stroke-linejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m8.25 3v6.75m0 0l-3-3m3 3l3-3M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
          </svg>
        </button>

        <!-- Delete Button -->
        <button 
          @click.stop="deleteConversation" 
          class="p-1 rounded hover:bg-red-100 text-red-600"
          title="Excluir Permanentemente"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
            <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
          </svg>
        </button>
      </div>

    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useUserStore } from '@/stores/user'
import api from '@/services/api' // Mantido para outras coisas, mas usaremos fetch manual aqui

const props = defineProps({
  conversation: { type: Object, required: true },
  active: { type: Boolean, default: false }
})

const emit = defineEmits(['refresh'])
const userStore = useUserStore()

const isAdmin = computed(() => {
  return userStore.currentUser?.role === 'admin'
})

async function unarchiveConversation() {
  if (!confirm('Deseja RESTAURAR esta conversa para a lista principal?')) return

  try {
    const token = localStorage.getItem('auth_token')
    const response = await fetch(`/api/conversations/${props.conversation.id}/unarchive`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    })

    const data = await response.json()

    if (response.ok) {
      emit('refresh', props.conversation.id)
    } else {
      throw new Error(data.error || 'Erro desconhecido no servidor')
    }
  } catch (error) {
    alert('Erro ao restaurar conversa: ' + error.message)
    console.error(error)
  }
}

async function archiveConversation() {
  if (!confirm('Tem certeza que deseja ARQUIVAR esta conversa?')) return

  try {
    const token = localStorage.getItem('auth_token')
    const response = await fetch(`/api/conversations/${props.conversation.id}/archive`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    })

    const data = await response.json()

    if (response.ok) {
      emit('refresh', props.conversation.id)
    } else {
      throw new Error(data.error || 'Erro desconhecido no servidor')
    }
  } catch (error) {
    alert('Erro ao arquivar conversa (Fetch): ' + error.message)
    console.error(error)
  }
}

async function deleteConversation() {
  if (!confirm('ATENÇÃO: Isso excluirá permanentemente a conversa e todo o histórico. Continuar?')) return

  try {
    const token = localStorage.getItem('auth_token')
    const response = await fetch(`/api/conversations/${props.conversation.id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    })

    const data = await response.json()

    if (response.ok) {
      emit('refresh', props.conversation.id)
    } else {
      throw new Error(data.error || 'Erro desconhecido no servidor')
    }
  } catch (error) {
    alert('Erro ao excluir conversa: ' + error.message)
  }
}

const initials = computed(() => {
  const name = props.conversation.contact_name || 'NA'
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
})

const lastMessagePreview = computed(() => {
  return props.conversation.last_message_preview || props.conversation.last_message_content || 'Sem mensagens'
})

function formatTime(timestamp) {
  if (!timestamp) return ''
  try {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true, locale: ptBR })
  } catch (error) {
    return ''
  }
}

function leadStatusLabel(status) {
  const labels = {
    novo: 'Novo',
    contatado: 'Contatado',
    qualificado: 'Qualificado',
    em_atendimento: 'Em Atendimento',
    orcamento: 'Orçamento',
    negociacao: 'Negociação',
    ganho: 'Ganho',
    perdido: 'Perdido'
  }
  return labels[status] || status
}

function statusClass(status) {
  const classes = {
    novo: 'bg-gray-100 text-gray-700',
    contatado: 'bg-blue-100 text-blue-700',
    qualificado: 'bg-green-100 text-green-700',
    em_atendimento: 'bg-yellow-100 text-yellow-700',
    orcamento: 'bg-yellow-100 text-yellow-700',
    negociacao: 'bg-orange-100 text-orange-700',
    ganho: 'bg-green-200 text-green-800',
    perdido: 'bg-red-100 text-red-700'
  }
  return classes[status] || 'bg-gray-100 text-gray-700'
}
</script>
